import { useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Copy, Fingerprint, KeyRound, Plug, Plus, QrCode, Upload, Pencil } from 'lucide-react';
import { DateTime } from 'luxon';
import type { AttendanceDayDto, AttendanceTotals, DeviceDto, DeviceKind, ImportResultDto, PunchDto } from '@workforce/shared';
import { api, qs } from '@/lib/api';
import { t } from '@/lib/i18n';
import { useAuth } from '@/lib/auth';
import { useTz } from '@/lib/timezone';
import { useDepartments, useEmployeeOptions } from '@/lib/queries';
import { useCurrentPeriod } from '@/lib/periods';
import {
  Badge,
  Button,
  Card,
  ConfirmDialog,
  EmptyState,
  ErrorBanner,
  Field,
  Input,
  Modal,
  Notice,
  PageHeader,
  Select,
  Spinner,
  StatCard,
  Tabs,
  Textarea,
  Toggle,
} from '@/components/ui';
import { ExportMenu } from '@/components/ExportMenu';
import { TimeZoneSelect } from '@/components/TimeZoneSelect';
import { AttendanceTable, RANGE_OPTIONS, TotalsBar, rangeFor, type RangePreset } from '@/components/AttendanceTable';

type Tab = 'day' | 'employee' | 'devices' | 'import';

export function AttendanceAdminPage() {
  const { can } = useAuth();
  const [params, setParams] = useSearchParams();
  const tab = (params.get('tab') as Tab) || 'day';
  const setTab = (v: Tab) => setParams((p) => { p.set('tab', v); return p; }, { replace: true });
  const canEdit = can('attendance.edit');
  const tabs: { value: Tab; label: string }[] = [
    { value: 'day', label: t('Daily overview') },
    { value: 'employee', label: t('By employee') },
    ...(canEdit ? [{ value: 'devices' as Tab, label: t('Devices') }, { value: 'import' as Tab, label: t('Import from machine') }] : []),
  ];
  return (
    <div>
      <PageHeader title={t('Attendance')} subtitle={t("Clock-ins from the fingerprint machine, the door QR code and the app, calculated against each person's schedule and time zone.")} />
      <Tabs tabs={tabs} value={tab} onChange={setTab} />
      {tab === 'day' && <DayTab />}
      {tab === 'employee' && <EmployeeTab canEdit={canEdit} />}
      {tab === 'devices' && canEdit && <DevicesTab />}
      {tab === 'import' && canEdit && <ImportTab />}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Daily overview
// ---------------------------------------------------------------------------
function DayTab() {
  const { companyZone } = useTz();
  const [date, setDate] = useState(() => DateTime.now().setZone(companyZone).toISODate()!);
  const [departmentId, setDepartmentId] = useState('');
  const depts = useDepartments();
  const q = useQuery({
    queryKey: ['attendance', 'day', date, departmentId],
    queryFn: () => api.get<{ date: string; days: AttendanceDayDto[]; totals: AttendanceTotals }>(`/attendance/day${qs({ date, departmentId })}`),
    refetchInterval: 60_000,
  });
  const days = q.data?.days ?? [];
  const count = (f: (d: AttendanceDayDto) => boolean) => days.filter(f).length;
  const shift = (n: number) => setDate(DateTime.fromISO(date).plus({ days: n }).toISODate()!);
  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-end gap-3">
        <Field label={t('Date')}>
          <div className="flex gap-1">
            <Button variant="secondary" onClick={() => shift(-1)} aria-label={t('Previous day')}>‹</Button>
            <Input type="date" value={date} onChange={(e) => e.target.value && setDate(e.target.value)} />
            <Button variant="secondary" onClick={() => shift(1)} aria-label={t('Next day')}>›</Button>
          </div>
        </Field>
        <Field label={t('Department')}>
          <Select value={departmentId} onChange={(e) => setDepartmentId(e.target.value)}>
            <option value="">{t('All departments')}</option>
            {depts.data?.map((d) => (
              <option key={d.id} value={d.id}>
                {d.name}
              </option>
            ))}
          </Select>
        </Field>
      </div>
      <div className="grid grid-cols-2 gap-4 md:grid-cols-5">
        <StatCard label={t('Working now')} value={count((d) => d.status === 'in_progress')} tone="green" />
        <StatCard label={t('Present')} value={count((d) => ['present', 'in_progress', 'incomplete'].includes(d.status))} tone="brand" />
        <StatCard label={t('Late')} value={count((d) => d.lateMinutes > 0)} tone="amber" />
        <StatCard label={t('Absent')} value={count((d) => d.status === 'absent')} tone="red" />
        <StatCard label={t('Missing punch')} value={count((d) => d.status === 'incomplete')} tone="amber" />
      </div>
      <Card bodyClass="p-0">
        <ErrorBanner error={q.error} />
        {q.isLoading ? (
          <Spinner />
        ) : days.length ? (
          <AttendanceTable days={days} showEmployee employeeLink={(d) => `/attendance?tab=employee&employee=${d.employeeId}`} />
        ) : (
          <EmptyState title={t('No employees')} />
        )}
      </Card>
      <p className="text-xs text-slate-500">
        {t("“Date” is each employee's own local date. Click a name to see their punches and correct them.")}
      </p>
    </div>
  );
}

// ---------------------------------------------------------------------------
// One employee over a period, with corrections
// ---------------------------------------------------------------------------
function EmployeeTab({ canEdit }: { canEdit: boolean }) {
  const [params, setParams] = useSearchParams();
  const employeeId = params.get('employee') ?? '';
  const { companyZone } = useTz();
  const opts = useEmployeeOptions();
  const periods = useCurrentPeriod();
  const [preset, setPreset] = useState<RangePreset>(() => (params.get('from') && params.get('to') ? 'custom' : 'period'));
  const [custom, setCustom] = useState(() =>
    params.get('from') && params.get('to') ? { from: params.get('from')!, to: params.get('to')! } : rangeFor('month', companyZone),
  );
  const range = rangeFor(preset, companyZone, custom, periods.data);
  const qc = useQueryClient();
  const q = useQuery({
    queryKey: ['attendance', 'employee', employeeId, range.from, range.to],
    queryFn: () => api.get<{ days: AttendanceDayDto[]; totals: AttendanceTotals }>(`/attendance/employees/${employeeId}${qs(range)}`),
    enabled: !!employeeId && (!preset.includes('period') || !periods.isLoading),
  });
  const [adding, setAdding] = useState<AttendanceDayDto | null>(null);
  const [voiding, setVoiding] = useState<PunchDto | null>(null);
  const refresh = () => qc.invalidateQueries({ queryKey: ['attendance'] });
  const voidM = useMutation({
    mutationFn: ({ id, reason }: { id: string; reason: string }) => api.post(`/attendance/punches/${id}/void`, { reason }),
    onSuccess: () => {
      setVoiding(null);
      refresh();
    },
  });
  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-end gap-3">
        <Field label={t('Employee')} className="min-w-64">
          <Select
            value={employeeId}
            onChange={(e) => setParams((p) => { p.set('employee', e.target.value); return p; }, { replace: true })}
          >
            <option value="">{t('Choose an employee…')}</option>
            {opts.data?.map((o) => (
              <option key={o.id} value={o.id}>
                {o.fullName} ({o.employeeCode})
              </option>
            ))}
          </Select>
        </Field>
        <Field label={t('Period')}>
          <Select value={preset} onChange={(e) => setPreset(e.target.value as RangePreset)}>
            {RANGE_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>
                {t(o.label)}
              </option>
            ))}
          </Select>
        </Field>
        {preset === 'custom' && (
          <>
            <Field label={t('From')}>
              <Input type="date" value={custom.from} onChange={(e) => setCustom((c) => ({ ...c, from: e.target.value }))} />
            </Field>
            <Field label={t('To')}>
              <Input type="date" value={custom.to} onChange={(e) => setCustom((c) => ({ ...c, to: e.target.value }))} />
            </Field>
          </>
        )}
        <div className="ms-auto">
          <ExportMenu kind="attendance" params={{ from: range.from, to: range.to, employee: employeeId || undefined }} label={employeeId ? t('Export') : t('Export everyone')} />
        </div>
      </div>
      {!employeeId ? (
        <Card>
          <EmptyState title={t('Choose an employee')} description={t('See every day with punches, worked hours, lateness and missing clock-outs.')} />
        </Card>
      ) : (
        <>
          <ErrorBanner error={q.error} />
          {q.isLoading && <Spinner />}
          {q.data && (
            <>
              <TotalsBar totals={q.data.totals} />
              <Card bodyClass="p-0">
                <AttendanceTable days={q.data.days} onAdd={canEdit ? setAdding : undefined} onVoid={canEdit ? (p) => setVoiding(p) : undefined} />
              </Card>
              <p className="text-xs text-slate-500">
                {t('Click a day to see its punches.')} {canEdit ? t('Corrections never delete anything: a wrong punch is voided (kept, crossed out) and a missing one is added — both with a reason in the audit log.') : ''}
              </p>
            </>
          )}
        </>
      )}
      {adding && <AddPunchModal day={adding} onClose={() => setAdding(null)} onDone={() => { setAdding(null); refresh(); }} />}
      <ConfirmDialog
        open={!!voiding}
        onClose={() => setVoiding(null)}
        onConfirm={(reason) => voiding && voidM.mutate({ id: voiding.id, reason })}
        title={t('Void punch')}
        message={t('The punch stays in the history (crossed out) and is no longer used in calculations.')}
        confirmLabel={t('Void punch')}
        danger
        requireReason
        loading={voidM.isPending}
        error={voidM.error}
      />
    </div>
  );
}

function AddPunchModal({ day, onClose, onDone }: { day: AttendanceDayDto; onClose: () => void; onDone: () => void }) {
  const { fmt } = useTz();
  const suggestedType = day.missingIn ? 'in' : 'out';
  const suggestedTime = day.missingIn ? day.scheduledStart : day.scheduledEnd;
  const [f, setF] = useState({
    date: day.date,
    time: suggestedTime ? fmt(suggestedTime, day.timezone, 'HH:mm') : '09:00',
    eventType: (day.punchCount === 0 ? 'in' : suggestedType) as 'in' | 'out',
    reason: '',
  });
  const m = useMutation({
    mutationFn: () => api.post('/attendance/punches', { employeeId: day.employeeId, ...f }),
    onSuccess: onDone,
  });
  return (
    <Modal
      open
      onClose={onClose}
      title={t('Add a punch')}
      size="sm"
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            {t('Cancel')}
          </Button>
          <Button loading={m.isPending} disabled={f.reason.trim().length < 3} onClick={() => m.mutate()}>
            {t('Add punch')}
          </Button>
        </>
      }
    >
      <ErrorBanner error={m.error} />
      <div className="space-y-4">
        <p className="text-sm text-slate-600">
          {t("Time is in the employee's time zone:")} <b>{day.timezone}</b>.
        </p>
        <div className="grid grid-cols-2 gap-3">
          <Field label={t('Date')}>
            <Input type="date" value={f.date} onChange={(e) => setF({ ...f, date: e.target.value })} />
          </Field>
          <Field label={t('Time')}>
            <Input type="time" value={f.time} onChange={(e) => setF({ ...f, time: e.target.value })} />
          </Field>
        </div>
        <Field label={t('Type')}>
          <Select value={f.eventType} onChange={(e) => setF({ ...f, eventType: e.target.value as 'in' | 'out' })}>
            <option value="in">{t('Clock in')}</option>
            <option value="out">{t('Clock out')}</option>
          </Select>
        </Field>
        <Field label={t('Reason')} required hint={t('Recorded in the audit log.')}>
          <Textarea rows={2} value={f.reason} onChange={(e) => setF({ ...f, reason: e.target.value })} maxLength={500} placeholder={t('e.g. Forgot to clock out — confirmed by the office manager')} />
        </Field>
      </div>
    </Modal>
  );
}

// ---------------------------------------------------------------------------
// Devices
// ---------------------------------------------------------------------------
const KIND_LABEL: Record<DeviceKind, string> = { fingerprint: 'Fingerprint machine', qr_kiosk: 'Door QR kiosk', api: 'Integration (API)' };
const KIND_ICON: Record<DeviceKind, JSX.Element> = {
  fingerprint: <Fingerprint className="h-4 w-4" />,
  qr_kiosk: <QrCode className="h-4 w-4" />,
  api: <Plug className="h-4 w-4" />,
};

function CopyField({ label, value }: { label: string; value: string; secret?: boolean }) {
  const [copied, setCopied] = useState(false);
  return (
    <Field label={label}>
      <div className="flex gap-2">
        <Input readOnly value={value} className="font-mono text-xs" onFocus={(e) => e.target.select()} />
        <Button
          variant="secondary"
          icon={<Copy className="h-4 w-4" />}
          onClick={() => {
            void navigator.clipboard?.writeText(value).then(() => {
              setCopied(true);
              setTimeout(() => setCopied(false), 1500);
            });
          }}
        >
          {copied ? t('Copied') : t('Copy')}
        </Button>
      </div>
    </Field>
  );
}

function SecretReveal({ device, secret }: { device: DeviceDto; secret: string }) {
  const origin = window.location.origin;
  if (device.kind === 'qr_kiosk') {
    return (
      <div className="space-y-3">
        <Notice tone="amber">{t('Open this link once on the door tablet. It is shown only now — if you lose it, issue a new key.')}</Notice>
        <CopyField label={t('Pairing link')} value={`${origin}/kiosk#key=${secret}`} secret />
        <p className="text-xs text-slate-500">{t("Tip: add the page to the tablet's home screen and keep the screen on. Restrict it to the office network below so a photo of the code is useless elsewhere.")}</p>
      </div>
    );
  }
  return (
    <div className="space-y-3">
      <Notice tone="amber">{t("This key is shown only now. Store it in the integration's settings.")}</Notice>
      <CopyField label={t('Device key')} value={secret} secret />
      <CopyField label={t('Endpoint (POST, header Authorization: Bearer <key>)')} value={`${origin}/api/attendance/push`} />
    </div>
  );
}

function AdmsHelp({ device }: { device: DeviceDto }) {
  const u = new URL(window.location.origin);
  return (
    <div className="space-y-2 rounded-lg bg-slate-50 p-3 text-xs text-slate-600">
      <p className="font-medium text-slate-800">{t('Live connection (ZKTeco and compatible “ADMS / Cloud server” machines)')}</p>
      <p>
        {t('On the machine:')} <i>Menu → Comm. → Cloud Server Setting</i> {t('(name varies by model). Enter:')}
      </p>
      <ul className="list-disc ps-5">
        <li>
          {t('Server address:')} <b className="font-mono">{u.hostname}</b>
        </li>
        <li>
          {t('Port:')} <b className="font-mono">{u.port || (u.protocol === 'https:' ? '443' : '80')}</b>
          {u.protocol === 'https:' ? ` (${t('HTTPS on')})` : ''}
        </li>
        <li>
          {t('Serial number registered here:')} <b className="font-mono">{device.serialNumber ?? t('— add it in Edit')}</b>
        </li>
      </ul>
      <p>{t('If the machine cannot connect, export the attendance log to a USB stick and use “Import from machine”.')}</p>
    </div>
  );
}

function DevicesTab() {
  const qc = useQueryClient();
  const { fmt } = useTz();
  const q = useQuery({ queryKey: ['attendance', 'devices'], queryFn: () => api.get<DeviceDto[]>('/attendance/devices') });
  const [editing, setEditing] = useState<DeviceDto | 'new' | null>(null);
  const [secret, setSecret] = useState<{ device: DeviceDto; key: string } | null>(null);
  const [rotating, setRotating] = useState<DeviceDto | null>(null);
  const rotate = useMutation({
    mutationFn: (d: DeviceDto) => api.post<{ key: string }>(`/attendance/devices/${d.id}/key`, {}),
    onSuccess: (r, d) => {
      setRotating(null);
      setSecret({ device: d, key: r.key });
      qc.invalidateQueries({ queryKey: ['attendance', 'devices'] });
    },
  });
  return (
    <div className="space-y-4">
      <div className="flex justify-end">
        <Button icon={<Plus className="h-4 w-4" />} onClick={() => setEditing('new')}>
          {t('Add device')}
        </Button>
      </div>
      <ErrorBanner error={q.error} />
      {q.isLoading ? (
        <Spinner />
      ) : !q.data?.length ? (
        <Card>
          <EmptyState
            icon={<Fingerprint className="h-6 w-6" />}
            title={t('No devices yet')}
            description={t('Add the office fingerprint machine (live or by file import) and/or a door tablet showing a rotating QR code.')}
          />
        </Card>
      ) : (
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
          {q.data.map((d) => (
            <Card
              key={d.id}
              title={
                <span className="flex items-center gap-2">
                  {KIND_ICON[d.kind]} {d.name} {!d.isActive && <Badge>{t('Disabled')}</Badge>}
                </span>
              }
              actions={
                <div className="flex gap-1">
                  {d.kind !== 'fingerprint' && (
                    <Button size="sm" variant="ghost" icon={<KeyRound className="h-3.5 w-3.5" />} onClick={() => setRotating(d)}>
                      {d.kind === 'qr_kiosk' ? t('New pairing link') : t('New key')}
                    </Button>
                  )}
                  <Button size="sm" variant="ghost" icon={<Pencil className="h-3.5 w-3.5" />} onClick={() => setEditing(d)}>
                    {t('Edit')}
                  </Button>
                </div>
              }
            >
              <dl className="grid grid-cols-2 gap-3 text-sm">
                <div><dt className="text-xs text-slate-500">{t('Type')}</dt><dd>{t(KIND_LABEL[d.kind])}</dd></div>
                <div><dt className="text-xs text-slate-500">{t('Time zone')}</dt><dd>{d.timezone}</dd></div>
                {(d.vendor || d.model) && <div><dt className="text-xs text-slate-500">{t('Model')}</dt><dd>{[d.vendor, d.model].filter(Boolean).join(' ')}</dd></div>}
                {d.serialNumber && <div><dt className="text-xs text-slate-500">{t('Serial number')}</dt><dd className="font-mono text-xs">{d.serialNumber}</dd></div>}
                <div><dt className="text-xs text-slate-500">{t('Last contact')}</dt><dd>{d.lastSeenAt ? fmt(d.lastSeenAt, d.timezone, 'dd LLL, HH:mm') : t('Never')}</dd></div>
                <div><dt className="text-xs text-slate-500">{t('Punches recorded')}</dt><dd>{d.punchCount}</dd></div>
                <div className="col-span-2"><dt className="text-xs text-slate-500">{t('Allowed networks')}</dt><dd className="font-mono text-xs">{d.allowedNetworks?.join(', ') || t('Any')}</dd></div>
              </dl>
              {d.kind === 'fingerprint' && !d.allowedNetworks?.length && (
                <div className="mt-4">
                  <Notice tone="amber">
                    {t("Set “Allowed networks” to the office's IP (or the office network, e.g. 192.168.1.0/24). Without it, anyone who knows this machine's serial number could send fake punches.")}
                  </Notice>
                </div>
              )}
              {d.kind === 'fingerprint' && <div className="mt-4"><AdmsHelp device={d} /></div>}
            </Card>
          ))}
        </div>
      )}
      {editing && (
        <DeviceModal
          device={editing === 'new' ? null : editing}
          onClose={() => setEditing(null)}
          onSaved={(d, key) => {
            setEditing(null);
            qc.invalidateQueries({ queryKey: ['attendance', 'devices'] });
            if (key && d.kind !== 'fingerprint') setSecret({ device: d, key });
          }}
        />
      )}
      <Modal open={!!secret} onClose={() => setSecret(null)} title={secret ? t('{name} — connection details', { name: secret.device.name }) : ''} footer={<Button onClick={() => setSecret(null)}>{t('Done')}</Button>}>
        {secret && <SecretReveal device={secret.device} secret={secret.key} />}
      </Modal>
      <ConfirmDialog
        open={!!rotating}
        onClose={() => setRotating(null)}
        onConfirm={() => rotating && rotate.mutate(rotating)}
        title={t('Issue a new key?')}
        message={t('The current key / pairing link stops working immediately. You will need to set up the device again with the new one.')}
        confirmLabel={t('Issue new key')}
        loading={rotate.isPending}
        error={rotate.error}
      />
    </div>
  );
}

function DeviceModal({ device, onClose, onSaved }: { device: DeviceDto | null; onClose: () => void; onSaved: (d: DeviceDto, key?: string) => void }) {
  const { companyZone } = useTz();
  const [f, setF] = useState({
    name: device?.name ?? '',
    kind: device?.kind ?? ('fingerprint' as DeviceKind),
    vendor: device?.vendor ?? '',
    model: device?.model ?? '',
    serialNumber: device?.serialNumber ?? '',
    timezone: device?.timezone ?? companyZone,
    trustPunchType: device?.trustPunchType ?? false,
    allowedNetworks: device?.allowedNetworks?.join(', ') ?? '',
    isActive: device?.isActive ?? true,
  });
  const m = useMutation({
    mutationFn: async () => {
      const body = {
        name: f.name,
        vendor: f.vendor || null,
        model: f.model || null,
        serialNumber: f.serialNumber || null,
        timezone: f.timezone,
        trustPunchType: f.trustPunchType,
        allowedNetworks: f.allowedNetworks.split(/[\s,]+/).filter(Boolean),
      };
      if (device) return { device: await api.patch<DeviceDto>(`/attendance/devices/${device.id}`, { ...body, isActive: f.isActive }), key: undefined };
      return api.post<{ device: DeviceDto; key: string }>('/attendance/devices', { ...body, kind: f.kind });
    },
    onSuccess: (r) => onSaved(r.device, r.key),
  });
  return (
    <Modal
      open
      onClose={onClose}
      title={device ? t('Edit {name}', { name: device.name }) : t('Add device')}
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>{t('Cancel')}</Button>
          <Button loading={m.isPending} disabled={!f.name || !f.timezone} onClick={() => m.mutate()}>{t('Save')}</Button>
        </>
      }
    >
      <ErrorBanner error={m.error} />
      <div className="space-y-4">
        {!device && (
          <Field label={t('Type')}>
            <Select value={f.kind} onChange={(e) => setF({ ...f, kind: e.target.value as DeviceKind })}>
              <option value="fingerprint">{t('Fingerprint machine')}</option>
              <option value="qr_kiosk">{t('Door QR kiosk (tablet)')}</option>
              <option value="api">{t('Other integration (API)')}</option>
            </Select>
          </Field>
        )}
        <Field label={t('Name')}>
          <Input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} placeholder={f.kind === 'qr_kiosk' ? t('Cairo office door') : t('Cairo office fingerprint')} />
        </Field>
        {f.kind === 'fingerprint' && (
          <>
            <div className="grid grid-cols-2 gap-3">
              <Field label={t('Brand')}><Input value={f.vendor} onChange={(e) => setF({ ...f, vendor: e.target.value })} placeholder={t('e.g. ZKTeco')} /></Field>
              <Field label={t('Model')}><Input value={f.model} onChange={(e) => setF({ ...f, model: e.target.value })} /></Field>
            </div>
            <Field label={t('Serial number')} hint={t('Needed only for the live connection. Shown on the machine under System Info / Device Info.')}>
              <Input value={f.serialNumber} onChange={(e) => setF({ ...f, serialNumber: e.target.value })} />
            </Field>
            <Toggle
              checked={f.trustPunchType}
              onChange={(v) => setF({ ...f, trustPunchType: v })}
              label={t("Use the machine's In/Out buttons")}
              description={t("Off (recommended): the system works out in/out from the order of punches, so people who press the wrong key don't break their day.")}
            />
          </>
        )}
        <Field label={t('Time zone of the device')} hint={t('Machines record local time without a zone; this is used to convert it.')}>
          <TimeZoneSelect value={f.timezone} onChange={(v) => setF({ ...f, timezone: v })} />
        </Field>
        {f.kind !== 'api' && (
          <Field
            label={t('Allowed networks (optional)')}
            hint={f.kind === 'qr_kiosk' ? t('Office public IP, e.g. 41.33.10.20 — scans from other networks are refused. Leave empty to allow any.') : t('Strongly recommended: only accept this machine from the office IP or office network (e.g. 192.168.1.0/24).')}
          >
            <Input value={f.allowedNetworks} onChange={(e) => setF({ ...f, allowedNetworks: e.target.value })} placeholder="41.33.10.20, 10.0.0.0/8" />
          </Field>
        )}
        {device && <Toggle checked={f.isActive} onChange={(v) => setF({ ...f, isActive: v })} label={t('Active')} description={t('Disabled devices are refused.')} />}
      </div>
    </Modal>
  );
}

// ---------------------------------------------------------------------------
// Import from the machine (USB export / attlog.dat / CSV)
// ---------------------------------------------------------------------------
function ImportTab() {
  const qc = useQueryClient();
  const { companyZone, fmt } = useTz();
  const devices = useQuery({ queryKey: ['attendance', 'devices'], queryFn: () => api.get<DeviceDto[]>('/attendance/devices') });
  const history = useQuery({
    queryKey: ['attendance', 'imports'],
    queryFn: () =>
      api.get<{ id: string; source: string; fileName: string | null; totalRows: number; imported: number; duplicates: number; unknownPins: { pin: string; count: number }[] | null; createdAt: string; device: string | null; by: string | null }[]>(
        '/attendance/imports',
      ),
  });
  const [deviceId, setDeviceId] = useState('');
  const [timezone, setTimezone] = useState(companyZone);
  const [file, setFile] = useState<{ name: string; content?: string; base64?: string } | null>(null);
  const [preview, setPreview] = useState<ImportResultDto | null>(null);
  const [done, setDone] = useState<ImportResultDto | null>(null);
  const [undo, setUndo] = useState<{ id: string; fileName: string | null; imported: number } | null>(null);
  const undoM = useMutation({
    mutationFn: (id: string) => api.del<{ removed: number }>(`/attendance/imports/${id}`),
    onSuccess: () => {
      setUndo(null);
      qc.invalidateQueries({ queryKey: ['attendance'] });
    },
  });
  const input = useRef<HTMLInputElement>(null);
  const fingerprints = devices.data?.filter((d) => d.kind === 'fingerprint') ?? [];

  // With a single fingerprint machine, pre-select it (and so its time zone).
  useEffect(() => {
    if (!deviceId && fingerprints.length === 1) setDeviceId(fingerprints[0]!.id);
  }, [fingerprints.length]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const d = devices.data?.find((x) => x.id === deviceId);
    if (d) setTimezone(d.timezone);
  }, [deviceId, devices.data]);

  const run = useMutation({
    mutationFn: (dryRun: boolean) =>
      api.post<ImportResultDto>('/attendance/import', {
        ...(file!.base64 ? { fileBase64: file!.base64 } : { content: file!.content }),
        fileName: file!.name,
        deviceId: deviceId || null,
        timezone,
        dryRun,
      }),
    onSuccess: (r, dryRun) => {
      if (dryRun) setPreview(r);
      else {
        setDone(r);
        setPreview(null);
        setFile(null);
        if (input.current) input.current.value = '';
        qc.invalidateQueries({ queryKey: ['attendance'] });
      }
    },
  });

  const onFile = (f: File | undefined) => {
    setPreview(null);
    setDone(null);
    if (!f) return setFile(null);
    const reader = new FileReader();
    if (/\.xlsx?$/i.test(f.name)) {
      // Excel reports are read on the server.
      reader.onload = () => setFile({ name: f.name, base64: String(reader.result ?? '').split(',')[1] ?? '' });
      reader.readAsDataURL(f);
      return;
    }
    reader.onload = () => {
      let text = String(reader.result ?? '');
      if (text.charCodeAt(0) === 0xfeff) text = text.slice(1);
      setFile({ name: f.name, content: text });
    };
    reader.readAsText(f);
  };

  return (
    <div className="grid grid-cols-1 gap-6 xl:grid-cols-3">
      <div className="space-y-4 xl:col-span-2">
        <Card title={t('Upload an attendance file')}>
          <ErrorBanner error={run.error} />
          <div className="space-y-4">
            <p className="text-sm text-slate-600">
              {t('Export the log from the fingerprint machine (USB stick → {file}, or the CSV/TXT from its software) and upload it. Re-importing the same file is safe: punches already recorded are skipped.', { file: 'attlog.dat' })}
            </p>
            <div className="grid gap-4 sm:grid-cols-2">
              <Field label={t('Machine')}>
                <Select value={deviceId} onChange={(e) => setDeviceId(e.target.value)}>
                  <option value="">{t('Not specified')}</option>
                  {fingerprints.map((d) => (
                    <option key={d.id} value={d.id}>{d.name}</option>
                  ))}
                </Select>
              </Field>
              <Field label={t('Time zone of the times in the file')}>
                <TimeZoneSelect value={timezone} onChange={setTimezone} />
              </Field>
            </div>
            <Field label={t('File')} hint={t('attlog.dat, .txt, .csv or the Excel report (.xls / .xlsx) — up to 10 MB. Employees are matched by their fingerprint machine ID (or employee ID).')}>
              <input
                ref={input}
                type="file"
                accept=".dat,.txt,.csv,.xls,.xlsx,text/plain,text/csv"
                onChange={(e) => onFile(e.target.files?.[0])}
                className="block w-full text-sm text-slate-600 file:me-3 file:rounded-lg file:border-0 file:bg-brand-50 file:px-3 file:py-2 file:text-sm file:font-medium file:text-brand-700 hover:file:bg-brand-100"
              />
            </Field>
            <div className="flex gap-2">
              <Button variant="secondary" icon={<Upload className="h-4 w-4" />} disabled={!file || !timezone} loading={run.isPending && run.variables === true} onClick={() => run.mutate(true)}>
                {t('Check file')}
              </Button>
              {preview && (
                <Button disabled={!preview.imported} loading={run.isPending && run.variables === false} onClick={() => run.mutate(false)}>
                  {preview.imported === 1 ? t('Import 1 punch') : t('Import {n} punches', { n: preview.imported })}
                </Button>
              )}
            </div>
          </div>
        </Card>
        {done && (
          <Notice tone="green">
            {done.duplicates
              ? done.imported === 1
                ? t('Imported 1 punch, {d} already recorded.', { d: done.duplicates })
                : t('Imported {n} punches, {d} already recorded.', { n: done.imported, d: done.duplicates })
              : done.imported === 1
                ? t('Imported 1 punch.')
                : t('Imported {n} punches.', { n: done.imported })}
          </Notice>
        )}
        {preview && <ImportPreview r={preview} />}
      </div>
      <Card title={t('Recent imports')} bodyClass="p-0">
        {history.data?.length ? (
          <ul className="divide-y divide-slate-100">
            {history.data.map((h) => (
              <li key={h.id} className="px-4 py-3 text-sm">
                <div className="flex items-center justify-between gap-2">
                  <span className="truncate font-medium">{h.fileName ?? (h.source === 'adms' ? t('Live from machine') : h.source)}</span>
                  <div className="flex shrink-0 items-center gap-2">
                    <span className="text-xs text-slate-500">{fmt(h.createdAt, null, 'dd LLL, HH:mm')}</span>
                    {h.source === 'file' && (
                      <Button variant="ghost" size="sm" onClick={() => setUndo(h)}>{t('Undo')}</Button>
                    )}
                  </div>
                </div>
                <p className="mt-0.5 text-xs text-slate-500">
                  {t('{imported} new · {duplicates} duplicate · {rows} rows', { imported: h.imported, duplicates: h.duplicates, rows: h.totalRows })}{h.device ? ` · ${h.device}` : ''}
                  {h.unknownPins?.length ? <span className="text-amber-700"> · {t('unknown IDs: {ids}', { ids: h.unknownPins.slice(0, 5).map((u) => u.pin).join(', ') })}</span> : null}
                </p>
              </li>
            ))}
          </ul>
        ) : (
          <p className="p-4 text-sm text-slate-500">{t('Nothing imported yet.')}</p>
        )}
      </Card>

      {undo && (
        <ConfirmDialog
          open
          danger
          title={t('Undo this import?')}
          message={t('This removes the {n} punches that “{file}” added. Live punches from the machines are not affected. You can re-upload the file later if needed.', { n: undo.imported, file: undo.fileName ?? t('import') })}
          confirmLabel={t('Undo import')}
          loading={undoM.isPending}
          onConfirm={() => undoM.mutate(undo.id)}
          onClose={() => setUndo(null)}
        />
      )}
    </div>
  );
}

function ImportPreview({ r }: { r: ImportResultDto }) {
  const { fmt } = useTz();
  return (
    <Card title={t('Check result — {format}', { format: r.format })}>
      <div className="mb-4 grid grid-cols-2 gap-3 sm:grid-cols-4">
        <StatCard label={t('Rows')} value={r.totalRows} />
        <StatCard label={t('New punches')} value={r.imported} tone="green" />
        <StatCard label={t('Already recorded')} value={r.duplicates} />
        <StatCard label={t('Problems')} value={r.errors.length + r.unknownPins.reduce((a, u) => a + u.count, 0)} tone={r.errors.length || r.unknownPins.length ? 'amber' : 'slate'} />
      </div>
      {!!r.inClosedPeriod && (
        <div className="mb-3">
          <Notice tone="amber">
            {r.inClosedPeriod === 1
              ? t("1 punch is dated in a closed reporting period. They are kept in the log but don't change the frozen figures unless that period is reopened.")
              : t("{n} punches are dated in a closed reporting period. They are kept in the log but don't change the frozen figures unless that period is reopened.", { n: r.inClosedPeriod })}
          </Notice>
        </div>
      )}
      {r.unknownPins.length > 0 && (
        <Notice tone="amber">
          {t("These machine IDs don't match any employee and will be skipped: {ids}. Set the “Fingerprint machine ID” on the employee's profile, then check again.", {
            ids: r.unknownPins.map((u) => `${u.pin}${u.name ? ` — ${u.name}` : ''} (${u.count})`).join(', '),
          })}
        </Notice>
      )}
      {r.errors.length > 0 && (
        <ul className="mt-3 list-disc ps-5 text-xs text-red-700">
          {r.errors.slice(0, 10).map((e) => (
            <li key={e.line}>{t('Line {line}: {message}', { line: e.line, message: t(e.message) })}</li>
          ))}
          {r.errors.length > 10 && <li>{t('…and {n} more', { n: r.errors.length - 10 })}</li>}
        </ul>
      )}
      {r.preview.length > 0 && (
        <div className="mt-4 overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead>
              <tr className="border-b border-slate-200 text-start text-xs uppercase text-slate-500">
                <th className="px-2 py-1.5">{t('Machine ID')}</th>
                <th className="px-2 py-1.5">{t('Employee')}</th>
                <th className="px-2 py-1.5">{t('Time')}</th>
                <th className="px-2 py-1.5">{t('Type')}</th>
              </tr>
            </thead>
            <tbody>
              {r.preview.map((p, i) => (
                <tr key={i} className="border-b border-slate-100">
                  <td className="px-2 py-1.5 font-mono text-xs">{p.pin}</td>
                  <td className="px-2 py-1.5">{p.employeeName ?? <span className="text-amber-700">{t('Unknown')}</span>}</td>
                  <td className="px-2 py-1.5 tabular-nums">{fmt(p.occurredAt, null, 'dd LLL yyyy, HH:mm')}</td>
                  <td className="px-2 py-1.5">{t(p.type)}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <p className="mt-2 text-xs text-slate-500">{t('First {n} rows shown · times displayed in your chosen time display.', { n: r.preview.length })}</p>
        </div>
      )}
    </Card>
  );
}
